import React from 'react'
import styled from 'styled-components'

import { ThemeColor } from 'theme/colors'

export interface CircleIconProps {
  size?: number
  percentage?: number
  stroke?: number
  color?: ThemeColor
  isFilled?: boolean
}

const StyledIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledSvg = styled.svg<{
  size: number
}>`
  position: relative;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`

const StyledCircle = styled.circle<{
  $strokeWidth: number
  $isFilled?: boolean
  color: ThemeColor
}>`
  width: 100%;
  height: 100%;

  fill: ${({ theme, $isFilled, color }) =>
    !$isFilled ? 'none' : theme.color[color]};
  stroke-width: ${({ $strokeWidth }) => $strokeWidth};
`

const StyledBackgroundCircle = styled(StyledCircle)<{ $strokeWidth: number }>`
  stroke: ${({ theme }) => theme.color.grey30};
`

const StyledPartialCircle = styled(StyledCircle)<{
  color: ThemeColor
  $strokeWidth: number
  $percentage: number
  $dashLength: number
}>`
  stroke-dashoffset: ${({ $percentage, $dashLength }) =>
    `calc(${$dashLength}px - (${$dashLength}px * ${$percentage}) / 100)`};
  stroke: ${({ theme, color }) => theme.color[color]};
  stroke-dasharray: ${({ $dashLength }) => $dashLength}px;
  transform-origin: center;
  transform: rotate(-90deg);
`

export const CircleIcon: React.FC<CircleIconProps> = ({
  percentage = 100,
  size = 12,
  stroke = 3,
  color = 'primary110',
  isFilled = false,
  ...props
}) => {
  const radius = size / 2
  const radiusWithoutStroke = radius - stroke / 2
  const dashLength = Math.ceil(2 * radiusWithoutStroke * Math.PI)

  return (
    <StyledIcon {...props}>
      <StyledSvg size={size}>
        <StyledBackgroundCircle
          color={color}
          cx={radius}
          cy={radius}
          $strokeWidth={stroke}
          r={radiusWithoutStroke}
        />
        <StyledPartialCircle
          color={color}
          $strokeWidth={stroke}
          $percentage={percentage}
          $dashLength={dashLength}
          cx={radius}
          cy={radius}
          r={radiusWithoutStroke}
          $isFilled={isFilled}
        />
      </StyledSvg>
    </StyledIcon>
  )
}
